import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_reassignWithOptimization,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import {
  buildAdvertisingQueryPredicate,
  getCurrentPlacementSource,
  updateAdvertisingCache,
} from '../utils/cacheHelpers';
import { applyCountDeltas, calculateCountDeltas, type CountDeltas } from '../utils/countCalculator';
import { useAwaitSpotlightOptimization } from './useAwaitSpotlightOptimization';

import type {
  BatchListingSpotlightUpdateResponseDto,
  ListingAlias,
  PlacementSource,
} from '@zg-rentals/ts-rental-manager-api-client';

type SpotlightCounts = {
  pinnedCount: number;
  autoCount: number;
  availableCount: number;
};

type MutationConfig = {
  labelFor: (id: string) => string;
  packageFor: (id: string) => string;
  spotlightCounts: SpotlightCounts | null;
  setSpotlightCounts: React.Dispatch<React.SetStateAction<SpotlightCounts | null>>;
};

/**
 * Hook that encapsulates all spotlight mutation logic with optimistic updates
 */
export const useSpotlightMutations = (config: MutationConfig) => {
  const { spotlightCounts, setSpotlightCounts } = config;
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();
  const awaitOptimization = useAwaitSpotlightOptimization();

  const { mutateAsync: batchPinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'pin listing to spotlight' },
    },
  });

  const { mutateAsync: batchUnpinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'unpin listing from spotlight' },
    },
  });

  const { mutateAsync: batchExcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'exclude listing from spotlight' },
    },
  });

  const { mutateAsync: batchUnexcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude({
    meta: {
      invalidates: ['getProperties', 'spotlightOverview'],
      context: { api: 'spotlight', details: 'unexclude listing from spotlight' },
    },
  });

  // Single-row changes go through the batch endpoints so every change kicks off engine work the table can wait on. A
  // batch call reports per-listing failure in its results rather than erroring, so a failed row throws to trigger
  // rollback.
  const forSingleListing =
    (
      batchMutation: (args: {
        postData: { listingAliases: Array<ListingAlias> };
      }) => Promise<BatchListingSpotlightUpdateResponseDto>,
    ) =>
    async (listingAlias: ListingAlias) => {
      const response = await batchMutation({ postData: { listingAliases: [listingAlias] } });
      if (!response.results.some((result) => result.listingAlias === listingAlias && result.success)) {
        throw new Error(`Spotlight change failed for ${listingAlias}`);
      }
      return response;
    };

  const pinMutation = forSingleListing(batchPinMutation);
  const unpinMutation = forSingleListing(batchUnpinMutation);
  const excludeMutation = forSingleListing(batchExcludeMutation);
  const unexcludeMutation = forSingleListing(batchUnexcludeMutation);

  const { mutateAsync: reassignMutation } =
    useRentalManagerUserPropertySpotlightV2WebApiHandler_reassignWithOptimization({
      meta: {
        invalidates: ['getProperties', 'spotlightOverview'],
        context: { api: 'spotlight', details: 'reassign spotlight with optimization' },
      },
    });

  /**
   * Optimistically update overview counts using setState updater function
   */
  const optimisticallyUpdateOverview = (deltas: CountDeltas) => {
    setSpotlightCounts((prev) => {
      if (!prev) return prev;
      return applyCountDeltas(prev, deltas);
    });
  };

  /**
   * Generic mutation executor with optimistic updates and rollback
   */
  const executeMutation = async <TSnapshot extends Record<string, unknown>>({
    id,
    action,
    mutationFn,
    targetSource,
    countDeltas,
    successMessage,
    successTone = 'success',
    errorMessage,
    applyOptimisticUpdate,
    captureSnapshot,
    restoreSnapshot,
  }: {
    id: string;
    action: 'pin' | 'unpin' | 'exclude' | 'unexclude';
    mutationFn: () => Promise<BatchListingSpotlightUpdateResponseDto>;
    targetSource: PlacementSource | null;
    countDeltas?: CountDeltas;
    successMessage: string;
    successTone?: 'success' | 'info';
    errorMessage: string;
    applyOptimisticUpdate: () => void;
    captureSnapshot: () => TSnapshot;
    restoreSnapshot: (snapshot: TSnapshot) => void;
  }) => {
    // Capture current state for rollback BEFORE any changes
    const snapshot = captureSnapshot();
    const previousCounts = spotlightCounts;
    const currentSource = getCurrentPlacementSource(queryClient, id);
    const deltas = countDeltas ?? calculateCountDeltas(action, currentSource);

    // Apply optimistic updates
    applyOptimisticUpdate();
    updateAdvertisingCache(queryClient, id, targetSource);
    optimisticallyUpdateOverview(deltas);

    try {
      await mutationFn();
      awaitOptimization();
      // Success - keep optimistic update
      enqueueToast(
        <Toast id={`spotlight-${action}`} tone={successTone}>
          {successMessage}
        </Toast>,
      );
    } catch (error) {
      // Rollback on error. Restore the exact pre-mutation counts — re-applying inverted deltas is not
      // symmetric because applyCountDeltas clamps at 0.
      restoreSnapshot(snapshot);
      updateAdvertisingCache(queryClient, id, currentSource ?? null);
      setSpotlightCounts(previousCounts);
      enqueueToast(
        <Toast id={`spotlight-${action}-error`} tone="critical">
          {errorMessage}
        </Toast>,
      );
      logError(error);
    }
  };

  return {
    executeMutation,
    optimisticallyUpdateOverview,
    pinMutation,
    unpinMutation,
    excludeMutation,
    unexcludeMutation,
    reassignMutation,
    updateAdvertisingCache: (id: string, source: PlacementSource | null) =>
      updateAdvertisingCache(queryClient, id, source),
    getCurrentPlacementSource: (id: string) => getCurrentPlacementSource(queryClient, id),
    invalidateAdvertisingQuery: (id: string) => {
      return queryClient.invalidateQueries(buildAdvertisingQueryPredicate(id));
    },
  };
};
